import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ChevronDown, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import heroBg from "@/assets/hero-bg.jpg";
import { trackEvent } from "@/lib/analytics";

const Hero = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  // Normalized pointer offset from section center, range -1..1
  const [pointer, setPointer] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    const el = sectionRef.current;
    if (!el) return;

    let ticking = false;
    let nextX = 0;
    let nextY = 0;

    const apply = () => {
      setPointer({ x: nextX, y: nextY });
      ticking = false;
    };

    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const halfW = rect.width / 2;
      const halfH = rect.height / 2;
      const nx = (e.clientX - rect.left - halfW) / halfW;
      const ny = (e.clientY - rect.top - halfH) / halfH;
      nextX = Math.min(1, Math.max(-1, nx));
      nextY = Math.min(1, Math.max(-1, ny));
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };

    const onLeave = () => {
      nextX = 0;
      nextY = 0;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  const { x: nx, y: ny } = pointer;
  const blobTransition =
    "transform 500ms cubic-bezier(0.22, 1, 0.36, 1), filter 500ms ease-out";

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background image */}
      <img
        src={heroBg}
        alt="AI-labbet hero background"
        className="absolute inset-0 w-full h-full object-cover opacity-40"
        loading="eager"
        width="1920"
        height="1080"
      />

      {/* Mesh gradient layer */}
      <div className="absolute inset-0 bg-gradient-mesh" />

      {/* Floating orbs — driven by mouse movement */}
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-primary/25 blur-3xl will-change-transform"
        style={{
          transform: `translate3d(${nx * 70}px, ${ny * 60}px, 0) scale(${1 + Math.abs(nx) * 0.08})`,
          filter: `blur(48px) hue-rotate(${nx * 12}deg)`,
          transition: blobTransition,
        }}
      />
      <div
        aria-hidden
        className="absolute top-1/3 -right-32 w-[32rem] h-[32rem] rounded-full blur-3xl will-change-transform"
        style={{
          backgroundColor: "hsl(var(--accent-violet) / 0.32)",
          transform: `translate3d(${nx * -90}px, ${ny * -75}px, 0) scale(${1 + Math.abs(ny) * 0.1})`,
          filter: `blur(48px) hue-rotate(${nx * -16}deg)`,
          transition: blobTransition,
        }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 left-1/3 w-[24rem] h-[24rem] rounded-full bg-primary/15 blur-3xl will-change-transform"
        style={{
          transform: `translate3d(${nx * 50}px, ${ny * -55}px, 0) scale(${1 + Math.abs(nx) * 0.07})`,
          filter: `blur(48px) hue-rotate(${ny * 10}deg)`,
          transition: blobTransition,
        }}
      />

      {/* Subtle dot grid for texture */}
      <div className="absolute inset-0 bg-grid-faint opacity-50" />

      {/* Bottom fade into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background to-transparent" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 text-center">
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 rounded-full border border-primary/30 bg-primary/5 backdrop-blur-sm text-sm text-foreground/90 animate-fade-in-up"
          style={{ animationDelay: "0ms" }}
        >
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          AI Strategy · Governance · Skilling
        </div>

        <h1
          className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight animate-fade-in-up"
          style={{ animationDelay: "120ms" }}
        >
          We create business value with{" "}
          <span className="text-gradient-brand">Responsible AI</span>
        </h1>

        <p
          className="text-lg sm:text-xl md:text-2xl text-muted-foreground mb-10 sm:mb-12 max-w-3xl mx-auto animate-fade-in-up"
          style={{ animationDelay: "240ms" }}
        >
          Empowering organizations through strategy, governance, and transformative AI journeys
        </p>

        <div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up"
          style={{ animationDelay: "360ms" }}
        >
          <Button variant="primary" size="xl" asChild className="transition-shadow duration-300 hover:shadow-glow-primary">
            <Link
              to="/contact"
              onClick={() => trackEvent("cta_click", { cta_id: "hero_strategy_session", location: "hero" })}
            >
              Book a Strategy Session
            </Link>
          </Button>
          <Button variant="tertiary" size="xl" asChild>
            <Link to="/services">Explore our services</Link>
          </Button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-muted-foreground animate-float-slow" aria-hidden="true">
        <ChevronDown className="w-6 h-6" />
      </div>
    </section>
  );
};

export default Hero;
